import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Store, LogOut, ShoppingBag, TrendingUp, Clock, CheckCircle, 
  XCircle, RefreshCw, Loader2, DollarSign, Users, Menu
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RestaurantDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [restaurant, setRestaurant] = useState(null);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    todayRevenue: 0
  });

  useEffect(() => {
    const session = localStorage.getItem('fudco_restaurant_session');
    if (!session) {
      navigate('/restaurant-login');
      return;
    }

    const sessionData = JSON.parse(session);
    setRestaurant(sessionData);
    fetchDashboardData(sessionData.phone);
  }, [navigate]);

  const fetchDashboardData = async (phone) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/restaurant/dashboard/${phone}`);
      setOrders(response.data.orders || []);
      setStats(response.data.stats || {
        totalOrders: 0,
        pendingOrders: 0,
        completedOrders: 0,
        todayRevenue: 0
      });
    } catch (error) {
      // Use mock data if API fails
      setOrders([
        { id: '1', order_number: 'FUD10001', customer: 'John Doe', items: '2x Biryani, 1x Naan', amount: 500, status: 'pending', time: '10 mins ago' },
        { id: '2', order_number: 'FUD10002', customer: 'Jane Smith', items: '1x Butter Chicken, 2x Roti', amount: 350, status: 'preparing', time: '15 mins ago' },
        { id: '3', order_number: 'FUD10003', customer: 'Mike Wilson', items: '3x Dosa, 1x Coffee', amount: 280, status: 'ready', time: '25 mins ago' },
      ]);
      setStats({
        totalOrders: 45,
        pendingOrders: 5,
        completedOrders: 40,
        todayRevenue: 12500
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('fudco_restaurant_session');
    toast.success('Logged out successfully');
    navigate('/restaurant-login');
  };

  const handleOrderAction = async (orderId, action) => {
    try {
      await axios.post(`${API}/restaurant/orders/${orderId}/status`, { status: action });
      toast.success(`Order ${action}!`);
      fetchDashboardData(restaurant.phone);
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      confirmed: 'bg-blue-100 text-blue-700',
      preparing: 'bg-purple-100 text-purple-700',
      ready: 'bg-green-100 text-green-700',
      picked_up: 'bg-indigo-100 text-indigo-700',
      delivered: 'bg-gray-100 text-gray-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    return (
      <Badge className={`${styles[status] || styles.pending} font-medium`}>
        {status?.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#2D8B2E]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Header */}
      <header className="bg-[#2D8B2E] text-white px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Store className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-bold">{restaurant?.name || 'Restaurant Dashboard'}</h1>
            <p className="text-xs text-white/70">+91 {restaurant?.phone}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => fetchDashboardData(restaurant?.phone)}
            className="text-white hover:bg-white/20"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleLogout}
            className="text-white hover:bg-white/20"
          >
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ShoppingBag className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalOrders}</p>
                <p className="text-xs text-gray-500">Total Orders</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pendingOrders}</p>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.completedOrders}</p>
                <p className="text-xs text-gray-500">Completed</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">₹{stats.todayRevenue}</p>
                <p className="text-xs text-gray-500">Today's Revenue</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Orders Table */}
        <Card className="p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[#2D8B2E]" />
              Recent Orders
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Order ID</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Customer</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Items</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Time</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.length > 0 ? orders.map((order) => (
                  <tr key={order.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono">{order.order_number}</td>
                    <td className="px-4 py-3 text-sm">{order.customer || order.user_name}</td>
                    <td className="px-4 py-3 text-sm max-w-xs truncate">{order.items || '-'}</td>
                    <td className="px-4 py-3 text-sm font-semibold">₹{order.amount}</td>
                    <td className="px-4 py-3">{getStatusBadge(order.status)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{order.time || order.created_at?.split('T')[0]}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {order.status === 'pending' && (
                          <Button 
                            size="sm" 
                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                            onClick={() => handleOrderAction(order.id, 'confirmed')}
                          >
                            Accept
                          </Button>
                        )}
                        {order.status === 'confirmed' && (
                          <Button 
                            size="sm" 
                            className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                            onClick={() => handleOrderAction(order.id, 'preparing')}
                          >
                            Prepare
                          </Button>
                        )}
                        {order.status === 'preparing' && (
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700 text-white text-xs"
                            onClick={() => handleOrderAction(order.id, 'ready')}
                          >
                            Ready
                          </Button>
                        )}
                        {(order.status === 'pending' || order.status === 'confirmed') && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50 text-xs"
                            onClick={() => handleOrderAction(order.id, 'cancelled')}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      No orders yet. Orders will appear here when customers place them.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <Card className="p-4 bg-white hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/restaurant-menu')}>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-xl">
                <Menu className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="font-semibold">Manage Menu</p>
                <p className="text-sm text-gray-500">Add or edit dishes</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-xl">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold">View Analytics</p>
                <p className="text-sm text-gray-500">Sales & performance</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold">Customer Reviews</p>
                <p className="text-sm text-gray-500">Feedback & ratings</p>
              </div>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default RestaurantDashboard;
